/**
 * MarginBreakdownChart — value against cost, per child of the current node, with the GM%
 * line on a right axis. TWO bars side by side: what the work is worth, and what it costs.
 * The gap between them is the margin.
 *
 * Margin is NOT a segment stacked on cost. It is not a quantity of the same kind as
 * material or machine time — stacking it makes the bar total revenue, hides cost behind a
 * subtraction, and crushes the four small components into slivers against one large block.
 * Separate bars put revenue and cost on the same baseline where they can be compared, which
 * is the whole question this chart exists to answer.
 *
 * `splitCost` breaks the cost bar into what it is made of (material · subcontract · machine
 * · labour · other, straight from FM_WOHEAD). Honest by construction: an unpriced child
 * (RA) simply has no cost bar and no GM dot; a loss is a cost bar taller than its value
 * bar, tinted, with a sub-zero GM% point — never a faked positive.
 */

import {
  Bar, CartesianGrid, Cell, ComposedChart, Customized, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { useTokens, type Tokens } from './useTokens'
import { useTheme } from '../../../contexts/ThemeContext'
import {
  buildRows, clippedCount, COST_KEY, fillsFor, gmDomain, kLabel, truncate, valueDomain,
  type BreakdownRow,
} from './marginBreakdownScale'
import MarginBreakdownDivider, { type Divider } from './MarginBreakdownDivider'
import MarginBreakdownLegend from './MarginBreakdownLegend'
import MarginBreakdownTooltip from './MarginBreakdownTooltip'
import { axisStyle, gridStroke } from './rechartsTheme'
import type { DrillRow } from '../../../types/metrics'
import { COST_COMPONENTS, type BreakdownBar } from '../../../viewmodels/orderBook'

const H = 340

/**
 * Bars take a FRACTION of their band, not a fixed pixel width. A 20px cap left a 120px-wide
 * month band as 100px of air: the wider the panel grew, the more of it was gap, and the
 * chart stopped using the space it had been given.
 *
 * Recharts applies barCategoryGap to BOTH sides of the band, so 10% spends 20% of the band
 * on gap and leaves ~80% to the value+cost pair.
 */
const BAND_GAP = '10%'

interface Props {
  bars: BreakdownBar[]
  /** What this plot is of — its accessible name. Two of these charts share the Margin
   * surface (the delivery calendar and the current drill level), so they must not
   * announce themselves identically. */
  label: string
  splitCost?: boolean
  marginLine?: boolean
  onBarClick?: (row: DrillRow) => void
  divider?: Divider
}

interface DotArgs {
  cx?: number
  cy?: number
  index?: number
  payload?: BreakdownRow
}

/** A GM% that left the scale: a triangle on the edge it left through, pointing that way.
 * It is not a data point at that height and must not read as one — hence the shape
 * change; the true figure stays in the tooltip. */
function gmDot(t: Tokens) {
  return ({ cx, cy, index, payload }: DotArgs) => {
    const key = `gm${index}`
    if (cx == null || cy == null || payload?.gmPlot == null) return <g key={key} />
    if (payload.clip) {
      const dy = payload.clip === 'above' ? -5 : 5
      return <polygon key={key} points={`${cx - 4},${cy - dy} ${cx + 4},${cy - dy} ${cx},${cy + dy}`} fill={t.red} stroke={t.bg} strokeWidth={1} />
    }
    return <circle key={key} cx={cx} cy={cy} r={3.2} fill={t.bg} stroke={payload.loss ? t.red : t.amber} strokeWidth={2} />
  }
}

interface ShapeArgs {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: BreakdownRow
  fill?: string
  fillOpacity?: number
  stroke?: string
}

/** Value is the ENVELOPE, not a category: outlined and neutral, so it can never be
 * mistaken for a cost component (Material shares the blue slot) and so the margin reads as
 * the headroom the cost bar has not filled.
 *
 * It is also the category's HIT TARGET — one activatable control per category, carrying
 * the row's name. A drillable bar that only responds to a mouse is a dead end for keyboard
 * and screen-reader users, and the cost bar does not repeat the role because two buttons
 * for one category is worse than none. */
function valueShape(pick?: (r: BreakdownRow) => void) {
  return ({ x, y, width, height, payload, fill, fillOpacity, stroke }: ShapeArgs) => {
    const onClick = pick && payload ? () => pick(payload) : undefined
    return (
      <g role={onClick ? 'button' : undefined} aria-label={onClick ? payload?.label : undefined}
        onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <rect className="recharts-rectangle" x={x} y={y} width={width} height={height} rx={2}
          fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={1} />
      </g>
    )
  }
}

/** The cost stack: its components where the caller asked for them, one block where not.
 * A loss recolours the COST bar, never the value bar — the bar that overshot is the one
 * that should say so. */
function costBars(rows: BreakdownRow[], splitCost: boolean, fills: string[], t: Tokens, click?: (r: BreakdownRow) => void) {
  const keys = splitCost ? COST_COMPONENTS : [COST_KEY]
  return keys.map((key, i) => (
    <Bar key={key} yAxisId="l" dataKey={key} className="mb-cost" name={splitCost ? key : 'Cost'} stackId="cost"
      fill={splitCost ? fills[i] : t.orange} fillOpacity={0.92} isAnimationActive={false}
      onClick={click ? (d: unknown) => click(d as BreakdownRow) : undefined}
      cursor={click ? 'pointer' : undefined}>
      {rows.map((r, ri) => (
        <Cell key={ri} fill={r.loss ? t.red : splitCost ? fills[i] : t.orange} />
      ))}
    </Bar>
  ))
}

/**
 * The tick angle is for labels that DO NOT FIT, so it applies only when they do not. 'May 26',
 * 'Kirkstall' and 'Oracle' read worse on the slant AND cost 48px of plot height; 25 customer
 * names genuinely need it. Width, not length: the label against its own band, so 2 long labels
 * stay flat and 24 short ones do not. The estimate is rough on purpose — it only has to be
 * right about "comfortably fits", and the fallback is the old behaviour, not a broken axis.
 */
const CHAR_PX = 6.2 // 10px JetBrains Mono
const PLOT_PX = 860 // the panel at its usual width, minus both value axes
const TICK_GAP = 10

function tickLayout(bars: BreakdownBar[]): { angle: number; textAnchor: 'end' | 'middle'; height: number } {
  const longest = bars.reduce((most, bar) => Math.max(most, truncate(bar.label).length), 0)
  return longest * CHAR_PX + TICK_GAP <= PLOT_PX / bars.length
    ? { angle: 0, textAnchor: 'middle', height: 30 }
    : { angle: -35, textAnchor: 'end', height: 78 }
}

/** The scaffold: grid, both axes and the tooltip. The £ axis and the GM% axis share one
 * set of gridlines — a second grid drawn over the first is noise, not information. */
function Scaffold({ bars, gmLo, gmHi, t }: { bars: BreakdownBar[]; gmLo: number; gmHi: number; t: Tokens }) {
  return (
    <>
      <CartesianGrid vertical={false} stroke={gridStroke} />
      <XAxis dataKey="label" tickFormatter={truncate} interval={0} {...tickLayout(bars)} {...axisStyle} />
      <YAxis yAxisId="l" domain={[0, valueDomain(bars)]} tickFormatter={kLabel} width={62} {...axisStyle} axisLine={false} />
      <YAxis yAxisId="r" orientation="right" domain={[gmLo, gmHi]} tickFormatter={(v: number) => `${Math.round(v)}%`}
        width={50} {...axisStyle} axisLine={false} />
      <Tooltip content={<MarginBreakdownTooltip />} cursor={{ fill: 'var(--panel2)', fillOpacity: 0.6 }} />
      {gmLo < 0 && gmHi > 0 && (
        <ReferenceLine yAxisId="r" y={0} stroke={t.mut2} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.6} />
      )}
    </>
  )
}

export default function MarginBreakdownChart({
  bars, label, splitCost = false, marginLine = false, onBarClick, divider,
}: Props) {
  const t = useTokens()
  const { theme } = useTheme()
  const fills = fillsFor(theme === 'light' ? 'light' : 'dark')
  const [gmLo, gmHi] = gmDomain(bars)
  const rows = buildRows(bars, splitCost, [gmLo, gmHi])
  const click = onBarClick ? (r: BreakdownRow) => onBarClick(r.bar.row) : undefined
  if (bars.length === 0) {
    return <div className="px-4 py-10 text-center text-xs text-mut2">No rows to chart at this level.</div>
  }
  return (
    <div role="img" aria-label={`${label} — value against cost, with gross-margin percent`}>
      <ResponsiveContainer width="100%" height={H}>
        <ComposedChart data={rows} margin={{ top: 16, right: 8, bottom: 8, left: 0 }}
          barCategoryGap={BAND_GAP} barGap={2}>
          {Scaffold({ bars, gmLo, gmHi, t })}
          <Bar yAxisId="l" dataKey="value" name="Value" className="mb-value" fill={t.mut} fillOpacity={0.16}
            stroke={t.mut} isAnimationActive={false} shape={valueShape(click)} />
          {costBars(rows, splitCost, fills, t, click)}
          {marginLine && (
            <Line yAxisId="l" dataKey="margin" name="Margin £" stroke={t.green} strokeWidth={2}
              connectNulls={false} isAnimationActive={false} dot={{ r: 3.2, fill: t.bg, stroke: t.green, strokeWidth: 2 }} />
          )}
          <Line yAxisId="r" dataKey="gmPlot" name="GM %" stroke={t.amber} strokeWidth={2}
            connectNulls={false} isAnimationActive={false} dot={gmDot(t)} activeDot={false} />
          {divider && <Customized component={<MarginBreakdownDivider divider={divider} barCount={bars.length} t={t} />} />}
        </ComposedChart>
      </ResponsiveContainer>
      <MarginBreakdownLegend bars={bars} fills={fills} splitCost={splitCost} marginLine={marginLine}
        clipped={clippedCount(bars, gmLo, gmHi)} />
    </div>
  )
}
